/* TicketDashboard.tsx — fullscreen triage board. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFeedback } from './store';
import { runExport, type ExportKind } from './exporters';
import {
  CSRF_HEADERS,
  JSON_HEADERS,
  SEVERITIES,
  SEVERITY_RANK,
  STATUSES,
  TAGS,
  TONE_STYLE,
  relTime,
  sevMeta,
  statusMeta,
} from './constants';
import type { Severity, Status, Ticket } from './types';

class AdminRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly canonicalTicket?: Ticket,
  ) {
    super(message);
  }
}

function ScreenshotView({ ticketId, src }: { ticketId: string; src: string }) {
  const { authHeaders } = useFeedback();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let created: string | null = null;
    setObjectUrl(null);
    setFailed(false);
    fetch(src, { credentials: 'include', cache: 'no-store', headers: authHeaders(CSRF_HEADERS) })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(String(res.status)))))
      .then((blob) => {
        if (!alive) return;
        if (typeof URL.createObjectURL !== 'function') throw new Error('no object URL support');
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      if (created && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(created);
    };
  }, [src, authHeaders]);

  if (failed) {
    return <div className="pt-shot-caption">The screenshot for this ticket could not be loaded.</div>;
  }
  if (!objectUrl) {
    return <div className="pt-shot-caption">Loading the screenshot…</div>;
  }
  return (
    <img className="pt-shot" src={objectUrl} alt={`Screenshot for ticket ${ticketId}`} />
  );
}

const SETTLED_STATUSES = new Set<Status>(['done', 'wontfix']);

export function isSettled(ticket: Pick<Ticket, 'status'>): boolean {
  return SETTLED_STATUSES.has(ticket.status);
}

/** Who to go back to. */
function reporterLabel(ticket: Ticket): string | null {
  return ticket.reporter_email || ticket.reporter_user_id || ticket.reporter || null;
}

function SevDot({ severity, size = 8 }: { severity: string; size?: number }) {
  const m = sevMeta(severity);
  return (
    <span
      title={m.label}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: m.dot,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

function StatusChip({ status, onClick }: { status: string; onClick?: () => void }) {
  const tone = TONE_STYLE[statusMeta(status).tone];
  return (
    <span
      className="pt-status-chip"
      onClick={onClick}
      style={{
        background: tone.bg,
        color: tone.fg,
        padding: '2px 9px',
        borderRadius: 100,
        fontSize: 10.5,
        fontWeight: 500,
        fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {statusMeta(status).label}
    </span>
  );
}

function TagPill({ tag }: { tag: string }) {
  return (
    <span
      style={{
        padding: '2px 7px',
        borderRadius: 4,
        border: '1px solid var(--p-line)',
        background: '#fff',
        fontSize: 10,
        fontFamily: 'JetBrains Mono, monospace',
        color: 'var(--p-ink-2)',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      {tag}
    </span>
  );
}

function SourceChip({ t }: { t: Ticket }) {
  if (t.source === 'staff-manual') {
    return <span className="pt-source-chip" title="Staff-recorded external feedback">EXTERNAL</span>;
  }
  if (t.kind === 'pin')
    return (
      <span className="pt-source-chip pin" title="Pin · click on screen">
        PIN
      </span>
    );
  if (t.source && t.source.startsWith('auto')) {
    const label =
      t.source === 'auto-error'
        ? 'AUTO · ERROR'
        : t.source === 'auto-rejection'
          ? 'AUTO · REJECT'
          : t.source === 'auto-deadclick'
            ? 'AUTO · DEAD'
            : 'AUTO';
    return (
      <span className="pt-source-chip auto" title={`Auto-captured · ${t.source}`}>
        {label}
        {(t.occurrences || 1) > 1 && <span className="x">×{t.occurrences}</span>}
      </span>
    );
  }
  return null;
}

const SOURCES = [
  { id: 'all', label: 'All sources' },
  { id: 'manual', label: 'Manual (FAB)' },
  { id: 'pin', label: 'Pins (click on screen)' },
  { id: 'auto', label: 'Auto (errors + dead clicks)' },
];
const SORTS = [
  { id: 'created', label: 'Newest first' },
  { id: 'updated', label: 'Recently updated' },
  { id: 'severity', label: 'Severity' },
];

const STATUS_ACTIONS = STATUSES.filter((status) => status.id !== 'triaged');

function mondayUtc(offsetWeeks = 0): string {
  const now = new Date();
  const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7) + offsetWeeks * 7);
  return monday.toISOString().slice(0, 10);
}

function downloadPacket(contents: string, filename: string, type: string): void {
  const href = URL.createObjectURL(new Blob([contents], { type }));
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}

export function TicketDashboard({ onNavigate }: { onNavigate?: (route: string) => void }) {
  const {
    tickets,
    updateTicket,
    adoptCanonicalTicket,
    bulkUpdateTickets,
    removeTicket,
    bulkRemoveTickets,
    clearTickets,
    addComment,
    reporter,
    setReporter,
    pushToast,
    setDashOpen,
    screenshotUrl,
    authHeaders,
    syncUrl,
    isZoeAdmin,
    forceSyncNow,
  } = useFeedback();

  // Opens on the tickets that still need attention, not the settled backlog.
  const [filterStatus, setFilterStatus] = useState('live');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [filterScreen, setFilterScreen] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showExternalIntake, setShowExternalIntake] = useState(false);
  const [externalTitle, setExternalTitle] = useState('');
  const [externalNote, setExternalNote] = useState('');
  const [externalChannel, setExternalChannel] = useState('Support email');
  const [triageOwner, setTriageOwner] = useState('');
  const [triageDelta, setTriageDelta] = useState('');
  const [triageWeek, setTriageWeek] = useState(() => mondayUtc(1));
  const [reviewWeek, setReviewWeek] = useState(() => mondayUtc());
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  // A canonical 409 replaces `active` with the durable winner.
  const preserveTriageDraftForTicket = useRef<string | null>(null);

  const screensWithTickets = useMemo(
    () => [...new Set(tickets.map((t) => t.route).filter(Boolean))].sort(),
    [tickets],
  );

  const filtered = useMemo(() => {
    let out = tickets.slice();
    if (filterStatus === 'live') out = out.filter((t) => !isSettled(t));
    else if (filterStatus === 'settled') out = out.filter((t) => isSettled(t));
    else if (filterStatus !== 'all') out = out.filter((t) => t.status === filterStatus);
    if (filterSeverity !== 'all') out = out.filter((t) => t.severity === filterSeverity);
    if (filterTag !== 'all') out = out.filter((t) => t.tag === filterTag);
    if (filterScreen !== 'all') out = out.filter((t) => t.route === filterScreen);
    if (filterSource !== 'all') {
      out = out.filter((t) => {
        if (filterSource === 'manual') return t.source === 'manual' || !t.source;
        if (filterSource === 'auto') return !!t.source && t.source.startsWith('auto');
        if (filterSource === 'pin') return t.kind === 'pin';
        return true;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((t) =>
        `${t.id} ${t.title} ${t.note} ${t.route} ${t.routeLabel} ${t.tag}`.toLowerCase().includes(q),
      );
    }
    out.sort((a, b) => {
      if (sortBy === 'updated') return (b.updatedAt || '').localeCompare(a.updatedAt || '');
      if (sortBy === 'severity') return SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });
    return out;
  }, [tickets, filterStatus, filterSeverity, filterTag, filterScreen, filterSource, search, sortBy]);

  const active = useMemo(
    () => filtered.find((t) => t.id === activeId) ?? null,
    [filtered, activeId],
  );

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: tickets.length, live: 0, settled: 0 };
    for (const s of STATUSES) c[s.id] = 0;
    for (const t of tickets) {
      c[t.status] = (c[t.status] || 0) + 1;
      if (isSettled(t)) c.settled += 1;
      else c.live += 1;
    }
    return c;
  }, [tickets]);

  useEffect(() => {
    if (activeId && !filtered.some((t) => t.id === activeId)) setActiveId(null);
  }, [filtered, activeId]);

  useEffect(() => setCommentText(''), [activeId]);
  useEffect(() => {
    if (!active) return;
    if (preserveTriageDraftForTicket.current === active.id) {
      preserveTriageDraftForTicket.current = null;
      return;
    }
    setTriageOwner(active.workflow?.owner ?? active.assignee ?? '');
    setTriageDelta(active.workflow?.named_delta ?? '');
    setTriageWeek(active.workflow?.review_week ?? mondayUtc(1));
  }, [active]);

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filtered.map((t) => t.id));
      const next = new Set<string>();
      let changed = false;
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filtered]);

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((prev) =>
      prev.size === filtered.length && filtered.length > 0
        ? new Set()
        : new Set(filtered.map((t) => t.id)),
    );
  const clearSelection = () => setSelected(new Set());

  const bulkStatus = (status: Status) => {
    if (!selected.size) return;
    bulkUpdateTickets([...selected], { status });
    pushToast({ kind: 'info', text: `${selected.size} ticket(s) → ${statusMeta(status).label}` });
    clearSelection();
  };
  const bulkDelete = () => {
    if (!selected.size) return;
    if (confirm(`Delete ${selected.size} ticket(s)?`)) {
      bulkRemoveTickets([...selected]);
      clearSelection();
    }
  };

  const submitComment = () => {
    if (!active || !commentText.trim()) return;
    addComment(active.id, { text: commentText.trim(), who: reporter || 'anonymous' });
    setCommentText('');
  };

  const doExport = (kind: ExportKind) => {
    const set = filtered.length === tickets.length ? tickets : filtered;
    if (!set.length) {
      pushToast({ kind: 'info', text: 'Nothing to export.' });
      setShowExportMenu(false);
      return;
    }
    runExport(kind, set);
    setShowExportMenu(false);
    pushToast({ kind: 'info', text: `Exported ${set.length} ticket(s)` });
  };

  const adminRequest = async (path: string, init?: RequestInit): Promise<Response> => {
    if (!syncUrl) throw new Error('The canonical feedback queue is unavailable.');
    const response = await fetch(`${syncUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers: authHeaders(init?.body ? JSON_HEADERS : CSRF_HEADERS),
    });
    if (!response.ok) {
      let detail = '';
      let canonicalTicket: Ticket | undefined;
      try {
        const payload = await response.clone().json() as {
          detail?: string;
          error?: string;
          issues?: Array<{ message?: string }>;
          canonical_ticket?: Ticket;
        };
        detail = payload.detail
          ?? payload.issues?.map((issue) => issue.message).filter(Boolean).join(' ')
          ?? payload.error
          ?? '';
        canonicalTicket = payload.canonical_ticket;
      } catch {
        // Non-JSON failures still retain the status-based fallback below.
      }
      throw new AdminRequestError(detail || `Request failed (${response.status}).`, response.status, canonicalTicket);
    }
    return response;
  };

  const submitExternalIntake = async () => {
    if (!externalTitle.trim() || !externalNote.trim() || !externalChannel.trim()) return;
    setAdminError(null);
    setAdminBusy(true);
    try {
      await adminRequest('/manual-intake', {
        method: 'POST',
        body: JSON.stringify({ title: externalTitle.trim(), note: externalNote.trim(), channel: externalChannel.trim() }),
      });
      setExternalTitle('');
      setExternalNote('');
      setShowExternalIntake(false);
      forceSyncNow();
      pushToast({ kind: 'info', text: 'External report captured for staff review.' });
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : 'External report could not be captured.');
    } finally {
      setAdminBusy(false);
    }
  };

  const submitTriage = async () => {
    if (!active || !triageOwner.trim() || !triageDelta.trim()) return;
    if (new Date(`${triageWeek}T00:00:00.000Z`).getUTCDay() !== 1) {
      setAdminError('Review week must be a Monday (UTC).');
      return;
    }
    setAdminError(null);
    setAdminBusy(true);
    try {
      await adminRequest(`/${encodeURIComponent(active.id)}/triage`, {
        method: 'POST',
        // The API compares this rendered revision inside Postgres.
        body: JSON.stringify({
          owner: triageOwner.trim(),
          named_delta: triageDelta.trim(),
          review_week: triageWeek,
          feedback_version: active._feedback_version ?? 0,
        }),
      });
      forceSyncNow();
      pushToast({ kind: 'info', text: 'Triaged and added to the weekly roadmap review.' });
    } catch (error) {
      if (error instanceof AdminRequestError && error.status === 409 && error.canonicalTicket) {
        preserveTriageDraftForTicket.current = active.id;
        adoptCanonicalTicket(error.canonicalTicket);
        setAdminError('Ticket changed, review and retry.');
        return;
      }
      setAdminError(error instanceof Error ? error.message : 'Triage could not be saved.');
    } finally {
      setAdminBusy(false);
    }
  };

  const exportReviewPacket = async (format: 'markdown' | 'csv') => {
    if (new Date(`${reviewWeek}T00:00:00.000Z`).getUTCDay() !== 1) {
      setAdminError('Review week must be a Monday (UTC).');
      return;
    }
    setAdminError(null);
    setAdminBusy(true);
    try {
      const response = await adminRequest(`/review-packet?week=${encodeURIComponent(reviewWeek)}&format=${format}`);
      downloadPacket(
        await response.text(),
        `zoe-roadmap-review-${reviewWeek}.${format === 'markdown' ? 'md' : 'csv'}`,
        format === 'markdown' ? 'text/markdown' : 'text/csv',
      );
    } catch (error) {
      pushToast({ kind: 'info', text: error instanceof Error ? error.message : 'Review packet could not be generated.' });
    } finally {
      setAdminBusy(false);
    }
  };

  return (
    <div className="proto-tickets">
      <div className="pt-topbar">
        <div className="pt-title">
          Tickets
          {/* Always visible, so a filtered list never reads as tickets vanishing. */}
          <span className="pt-mono pt-muted" style={{ marginLeft: 10 }}>
            {counts.live} open · {counts.settled} settled · {filtered.length} shown
          </span>
        </div>
        <input
          className="pt-search"
          type="text"
          placeholder="Search by id, title, note, screen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="pt-reporter">
          <label className="pt-mono pt-muted" style={{ fontSize: 9.5 }}>
            YOU
          </label>
          <input
            type="text"
            placeholder="your name"
            value={reporter}
            onChange={(e) => setReporter(e.target.value)}
          />
        </div>
        <div className="pt-export-wrap">
          <button className="pt-btn" aria-label="Export menu" onClick={() => setShowExportMenu((v) => !v)}>
            ↓ Export {filtered.length < tickets.length ? `(${filtered.length})` : ''} ▾
          </button>
          {showExportMenu && (
            <div className="pt-export-menu">
              <button onClick={() => doExport('json')}>↓ JSON · structured array</button>
              <button onClick={() => doExport('markdown')}>↓ Markdown · grouped by status</button>
              <div className="pt-export-divider" />
              <button onClick={() => doExport('linear')}>↓ Linear CSV · import-ready</button>
              <button onClick={() => doExport('github')}>↓ GitHub Issues · paste-by-paste</button>
              <button onClick={() => doExport('jira')}>↓ Jira CSV · import-ready</button>
            </div>
          )}
        </div>
        {isZoeAdmin && (
          <button className="pt-btn" onClick={() => setShowExternalIntake((value) => !value)}>
            + External report
          </button>
        )}
        {isZoeAdmin && (
          <div className="pt-export-wrap">
            <button className="pt-btn" aria-label="Weekly review packet" onClick={() => exportReviewPacket('markdown')} disabled={adminBusy}>
              ↓ Review packet
            </button>
          </div>
        )}
        <button
          className="pt-btn"
          onClick={() => setDashOpen(false)}
          aria-label="Close dashboard"
          title="Close (Esc)"
        >
          ✕ Close
        </button>
      </div>

      {isZoeAdmin && showExternalIntake && (
        <div className="pt-bulkbar" aria-label="External feedback intake">
          <input className="pt-input" aria-label="External channel" placeholder="External channel" maxLength={120} value={externalChannel} onChange={(e) => setExternalChannel(e.target.value)} />
          <input className="pt-input" aria-label="External report title" placeholder="Report title" maxLength={240} value={externalTitle} onChange={(e) => setExternalTitle(e.target.value)} />
          <input className="pt-input" aria-label="External report details" placeholder="Report details" maxLength={4000} value={externalNote} onChange={(e) => setExternalNote(e.target.value)} />
          <button className="pt-btn sm" disabled={adminBusy || !externalTitle.trim() || !externalNote.trim() || !externalChannel.trim()} onClick={submitExternalIntake}>Capture</button>
        </div>
      )}
      {isZoeAdmin && adminError && <div className="pt-bulkbar" role="alert">{adminError}</div>}
      {isZoeAdmin && (
        <div className="pt-bulkbar" aria-label="Weekly roadmap review packet">
          <span className="pt-mono pt-muted">REVIEW WEEK</span>
          <input className="pt-input" type="date" value={reviewWeek} onChange={(e) => setReviewWeek(e.target.value)} />
          <button className="pt-btn sm ghost" disabled={adminBusy} onClick={() => exportReviewPacket('markdown')}>Markdown</button>
          <button className="pt-btn sm ghost" disabled={adminBusy} onClick={() => exportReviewPacket('csv')}>CSV</button>
        </div>
      )}

      <div className={'pt-panes' + (!active ? ' list-only' : '')}>
        {/* Filter rail */}
        <aside className="pt-rail">
          <div className="pt-rail-section">
            <h5>STATUS</h5>
            <div className="pt-rail-list">
              <button
                className={'pt-rail-item' + (filterStatus === 'live' ? ' active' : '')}
                onClick={() => setFilterStatus('live')}
              >
                Open <span className="n">{counts.live}</span>
              </button>
              <button
                className={'pt-rail-item' + (filterStatus === 'settled' ? ' active' : '')}
                onClick={() => setFilterStatus('settled')}
              >
                Settled <span className="n">{counts.settled}</span>
              </button>
              <div className="pt-rail-divider" />
              <button
                className={'pt-rail-item' + (filterStatus === 'all' ? ' active' : '')}
                onClick={() => setFilterStatus('all')}
              >
                All <span className="n">{counts.all}</span>
              </button>
              {STATUS_ACTIONS.map((s) => (
                <button
                  key={s.id}
                  className={'pt-rail-item' + (filterStatus === s.id ? ' active' : '')}
                  onClick={() => setFilterStatus(s.id)}
                >
                  {s.label} <span className="n">{counts[s.id] || 0}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pt-rail-section">
            <h5>SEVERITY</h5>
            <div className="pt-rail-list">
              <button
                className={'pt-rail-item' + (filterSeverity === 'all' ? ' active' : '')}
                onClick={() => setFilterSeverity('all')}
              >
                All severities
              </button>
              {SEVERITIES.map((s) => (
                <button
                  key={s.id}
                  className={'pt-rail-item' + (filterSeverity === s.id ? ' active' : '')}
                  onClick={() => setFilterSeverity(s.id)}
                >
                  <SevDot severity={s.id} />
                  <span style={{ marginLeft: 8 }}>{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pt-rail-section">
            <h5>TYPE</h5>
            <div className="pt-rail-list">
              <button
                className={'pt-rail-item' + (filterTag === 'all' ? ' active' : '')}
                onClick={() => setFilterTag('all')}
              >
                All tags
              </button>
              {TAGS.map((t) => (
                <button
                  key={t}
                  className={'pt-rail-item' + (filterTag === t ? ' active' : '')}
                  onClick={() => setFilterTag(t)}
                  style={{ textTransform: 'capitalize' }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-rail-section">
            <h5>SOURCE</h5>
            <div className="pt-rail-list">
              {SOURCES.map((s) => (
                <button
                  key={s.id}
                  className={'pt-rail-item' + (filterSource === s.id ? ' active' : '')}
                  onClick={() => setFilterSource(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {screensWithTickets.length > 0 && (
            <div className="pt-rail-section">
              <h5>SCREEN</h5>
              <div className="pt-rail-list">
                <button
                  className={'pt-rail-item' + (filterScreen === 'all' ? ' active' : '')}
                  onClick={() => setFilterScreen('all')}
                >
                  All screens
                </button>
                {screensWithTickets.map((s) => (
                  <button
                    key={s}
                    className={'pt-rail-item' + (filterScreen === s ? ' active' : '')}
                    onClick={() => setFilterScreen(s)}
                  >
                    <span className="pt-mono">{s}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pt-rail-section">
            <h5>SORT</h5>
            <div className="pt-rail-list">
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  className={'pt-rail-item' + (sortBy === s.id ? ' active' : '')}
                  onClick={() => setSortBy(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-rail-section">
            <button
              className="pt-btn ghost full"
              onClick={() => {
                if (confirm(`Delete all ${tickets.length} tickets? This can't be undone.`)) {
                  clearTickets();
                  clearSelection();
                }
              }}
            >
              Clear all tickets
            </button>
          </div>
        </aside>

        {/* List */}
        <section className="pt-list">
          {selected.size > 0 && (
            <div className="pt-bulkbar">
              <span className="pt-mono pt-muted">{selected.size} selected</span>
              <button className="pt-btn sm ghost" onClick={clearSelection}>
                Clear
              </button>
              <span className="pt-bulkbar-sep" />
              <span className="pt-mono pt-muted" style={{ fontSize: 9.5 }}>
                SET STATUS
              </span>
              {STATUS_ACTIONS.map((s) => (
                <button key={s.id} className="pt-btn sm" onClick={() => bulkStatus(s.id)}>
                  {s.label}
                </button>
              ))}
              <span className="pt-bulkbar-sep" />
              <button className="pt-btn sm danger" onClick={bulkDelete}>
                Delete
              </button>
            </div>
          )}

          <div className="pt-list-head">
            <input
              type="checkbox"
              checked={selected.size === filtered.length && filtered.length > 0}
              onChange={toggleAll}
              aria-label="Select all"
            />
            <span className="pt-mono pt-muted">
              {filtered.length} ticket{filtered.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="pt-list-body">
            {filtered.length === 0 ? (
              <div className="pt-empty">
                <div style={{ fontSize: 36, color: 'var(--p-ink-3)', marginBottom: 14 }}>·</div>
                <div style={{ fontSize: 14, color: 'var(--p-ink-2)' }}>
                  No tickets match these filters.
                </div>
                <div style={{ fontSize: 12, color: 'var(--p-ink-3)' }}>
                  Press <span className="pt-mono">f</span> on any screen to capture a new one.
                </div>
              </div>
            ) : (
              filtered.map((t) => (
                <div
                  key={t.id}
                  className={'pt-row' + (activeId === t.id ? ' active' : '')}
                  onClick={() => setActiveId(t.id)}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={() => toggleOne(t.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${t.id}`}
                  />
                  <SevDot severity={t.severity} />
                  <StatusChip status={t.status} />
                  <TagPill tag={t.tag} />
                  <SourceChip t={t} />
                  <span className="pt-row-title">
                    {t.title || <em style={{ color: 'var(--p-ink-3)' }}>untitled</em>}
                  </span>
                  {t.triage_sla?.overdue && (
                    <span className="pt-mono" style={{ color: 'var(--p-red)', fontSize: 9.5 }}>
                      TRIAGE OVERDUE
                    </span>
                  )}
                  <span className="pt-row-screen pt-mono">{t.route}</span>
                  <span className="pt-row-when pt-mono pt-muted">{relTime(t.createdAt)}</span>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Detail */}
        {active && (
          <section className="pt-detail" aria-label={`Ticket detail for ${active.id}`}>
            <>
              <div className="pt-detail-head">
                <div className="pt-mono pt-muted" style={{ fontSize: 10.5 }}>
                  {active.id}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <SevDot severity={active.severity} />
                  <StatusChip status={active.status} />
                  <TagPill tag={active.tag} />
                  <SourceChip t={active} />
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button
                      className="pt-btn sm ghost"
                      aria-label="Close ticket detail"
                      onClick={() => setActiveId(null)}
                    >
                      ← List
                    </button>
                    {active.route && onNavigate && (
                      <button
                        className="pt-btn sm"
                        aria-label="Jump to screen"
                        onClick={() => {
                          onNavigate(active.route);
                          setDashOpen(false);
                        }}
                      >
                        → Jump to {active.route}
                      </button>
                    )}
                    <button
                      className="pt-btn sm ghost danger"
                      onClick={() => {
                        if (confirm('Delete this ticket?')) removeTicket(active.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <h2 style={{ fontFamily: 'Instrument Serif, serif', fontSize: 26, margin: '6px 0 0' }}>
                  {active.title || <em>untitled</em>}
                </h2>
                <div className="pt-mono pt-muted" style={{ fontSize: 11 }}>
                  {/* Stamped by the API from the session. */}
                  {reporterLabel(active) && <>Reported by {reporterLabel(active)} · </>}
                  Reported {new Date(active.createdAt).toLocaleString()}
                  {active.updatedAt !== active.createdAt && ` · updated ${relTime(active.updatedAt)}`}
                  {active.viewport && ` · ${active.viewport}`}
                </div>
              </div>

              <div className="pt-detail-body">
                {/* Screenshots arrived after ~250 tickets already existed. */}
                {active.screenshotId && screenshotUrl && (
                  <div className="pt-section">
                    <h6>SCREENSHOT</h6>
                    <ScreenshotView
                      ticketId={active.id}
                      src={`${screenshotUrl}/${encodeURIComponent(active.screenshotId)}`}
                    />
                    <div className="pt-shot-caption">
                      The screen as the reporter saw it, at the moment the ticket was raised.
                    </div>
                  </div>
                )}

                <div className="pt-section">
                  <h6>NOTE</h6>
                  <div className="pt-note">{active.note}</div>
                </div>

                <div className="pt-section">
                  <h6>STATUS</h6>
                  <div className="pt-status-row">
                    {STATUS_ACTIONS.map((s) => (
                      <button
                        key={s.id}
                        className={'pt-statusbtn' + (active.status === s.id ? ' active' : '')}
                        onClick={() => updateTicket(active.id, { status: s.id })}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-section">
                  <h6>SEVERITY</h6>
                  <div className="pt-status-row">
                    {SEVERITIES.map((s) => (
                      <button
                        key={s.id}
                        className={'pt-statusbtn' + (active.severity === s.id ? ' active' : '')}
                        onClick={() => updateTicket(active.id, { severity: s.id as Severity })}
                      >
                        <SevDot severity={s.id} />
                        <span style={{ marginLeft: 6 }}>{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-section">
                  <h6>ASSIGNEE</h6>
                  <input
                    className="pt-input"
                    placeholder="unassigned"
                    value={active.assignee || ''}
                    onChange={(e) =>
                      updateTicket(active.id, { assignee: e.target.value || null })
                    }
                  />
                </div>

                {active.triage_sla && (
                  <div className="pt-section">
                    <h6>TRIAGE SLA</h6>
                    <div className="pt-mono pt-muted">
                      Due {active.triage_sla.due_at}{active.triage_sla.overdue ? ' · OVERDUE' : ''}
                    </div>
                  </div>
                )}

                {isZoeAdmin && (
                  <div className="pt-section">
                    <h6>ROADMAP TRIAGE</h6>
                    <div className="pt-status-row" style={{ alignItems: 'stretch' }}>
                      <input className="pt-input" placeholder="Owner" maxLength={200} value={triageOwner} onChange={(e) => setTriageOwner(e.target.value)} />
                      <input className="pt-input" placeholder="Named roadmap delta" maxLength={500} value={triageDelta} onChange={(e) => setTriageDelta(e.target.value)} />
                      <input className="pt-input" type="date" value={triageWeek} onChange={(e) => setTriageWeek(e.target.value)} />
                      <button className="pt-btn sm" disabled={adminBusy || !triageOwner.trim() || !triageDelta.trim()} onClick={submitTriage}>Mark triaged</button>
                    </div>
                    {active.workflow?.triaged_at && (
                      <div className="pt-mono pt-muted" style={{ marginTop: 8 }}>
                        {active.workflow.named_delta} · {active.workflow.owner} · review {active.workflow.review_week} · triaged {active.workflow.triaged_at}
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-section">
                  <h6>COMMENTS · {active.comments?.length || 0}</h6>
                  <div className="pt-comments">
                    {(active.comments || []).map((c) => (
                      <div key={c.id} className="pt-comment">
                        <div className="pt-comment-head">
                          <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--p-ink)' }}>
                            {c.who}
                          </span>
                          <span className="pt-mono pt-muted">{relTime(c.at)}</span>
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: 'var(--p-ink-2)',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {c.text}
                        </div>
                      </div>
                    ))}
                    <div className="pt-compose">
                      <textarea
                        placeholder={`Comment as ${reporter || 'anonymous'}…`}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submitComment();
                        }}
                      />
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}
                      >
                        <span className="pt-mono pt-muted">⌘↵ to send</span>
                        <button
                          className="pt-btn sm"
                          style={{ marginLeft: 'auto' }}
                          disabled={!commentText.trim()}
                          onClick={submitComment}
                        >
                          Post comment
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          </section>
        )}
      </div>
    </div>
  );
}
